/**
 * Base API client for making HTTP requests
 */

import { API_CONFIG } from '../config';

// Use the consistent API base URL from config
const API_BASE_URL = API_CONFIG.BASE_URL;

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  body?: unknown;
  params?: Record<string, unknown>;
};

// Helper function to ensure valid URL formation
function ensureValidUrl(baseUrl: string, endpoint: string): string {
  // Remove trailing slash from base URL if present
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  // Remove leading slash from endpoint if present
  const path = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  // Join with a slash
  return `${base}/${path}`;
}

// Helper function to handle 401 errors and redirect to login
const handle401Error = () => {
  if (typeof window !== 'undefined') {
    // Clear auth tokens from localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('token_last_validated');
    
    // Redirect to login page
    window.location.href = '/login';
  }
};

/**
 * Process API response based on content type and encoding
 * Handles JSON parsing and decompression
 */
async function processResponse(response: Response): Promise<unknown> {
  // Check for compression encoding
  const contentEncoding = response.headers.get('content-encoding');
  const contentType = response.headers.get('content-type');
  
  // For compressed responses, use response.json() which handles decompression automatically
  if (contentEncoding) {
    try {
      return await response.json();
    } catch (error) {
      console.error('Error parsing compressed response:', error);
      const text = await response.text();
      return text ? JSON.parse(text) : {};
    }
  }
  
  // For uncompressed responses, use standard JSON parsing
  if (contentType?.includes('application/json')) {
    return await response.json();
  }
  
  // Return text for non-JSON responses
  return await response.text();
}

// Get auth token from local storage
const getAuthToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('auth_token');
};

// Add authorization header if token exists
const getAuthHeaders = (): Record<string, string> => {
  const token = getAuthToken();
  if (token) {
    return { 'Authorization': `Bearer ${token}` };
  }
  return {};
};

// API client with centralized implementation
export const apiClient = {
  get: async (endpoint: string, options: { params?: Record<string, unknown> } = {}) => {
    try {
      const urlString = ensureValidUrl(API_BASE_URL, endpoint);
      const url = new URL(urlString);
      
      if (options.params) {
        Object.entries(options.params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            url.searchParams.append(key, String(value));
          }
        });
      }
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Accept-Encoding': 'gzip, br', // Request compressed responses
          ...getAuthHeaders()
        },
      });
      
      if (!response.ok) {
        // Handle 401 Unauthorized errors
        if (response.status === 401) {
          handle401Error();
          throw new Error('Authentication failed. Redirecting to login...');
        }
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await processResponse(response);
      return { data };
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  },
  
  post: async (endpoint: string, data: unknown) => {
    try {
      const urlString = ensureValidUrl(API_BASE_URL, endpoint);
      const url = new URL(urlString);
      
      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Accept-Encoding': 'gzip, br', // Request compressed responses
          ...getAuthHeaders()
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        // Handle 401 Unauthorized errors
        if (response.status === 401) {
          handle401Error();
          throw new Error('Authentication failed. Redirecting to login...');
        }
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const responseData = await processResponse(response);
      return { data: responseData };
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }
};

/**
 * Handles API requests with error handling and response parsing
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { method = 'GET', headers = {}, body, params } = options;
  
  try {
    // Build URL with query parameters
    const urlString = ensureValidUrl(API_BASE_URL, endpoint);
    const url = new URL(urlString);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          url.searchParams.append(key, String(value));
        }
      });
    }
    
    // Request configuration
    const config: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Accept-Encoding': 'gzip, br', // Request compressed responses
        ...getAuthHeaders(),
        ...headers,
      },
    };
    
    if (body) {
      config.body = JSON.stringify(body);
    }
    
    const response = await fetch(url.toString(), config);
    
    // Handle non-2xx responses
    if (!response.ok) {
      // Handle 401 Unauthorized errors
      if (response.status === 401) {
        handle401Error();
        throw new Error('Authentication failed. Redirecting to login...');
      }
      
      const errorData = await processResponse(response).catch(() => ({}));
      // Use type assertion with Record to avoid 'any'
      const typedErrorData = errorData as Record<string, unknown>;
      throw new Error(
        (typeof typedErrorData.message === 'string' ? typedErrorData.message : '') || 
        `API error: ${response.status} ${response.statusText}`
      );
    }
    
    // Process and return response
    return await processResponse(response) as T;
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}
